import api from './api';

// ============================================
// TYPES
// ============================================

export type MovementType = 'entrada' | 'saida' | 'ajuste' | 'venda' | 'devolucao';
export type AlertType = 'estoque_baixo' | 'sem_estoque' | 'excesso';
export type StockStatus = 'ok' | 'low' | 'out';

export interface InventoryProduct {
  id: string;
  name: string;
  price: number;
  image?: string;
  storeId?: string | null;
  storeName: string;
  categoryId?: string | null;
  categoryName: string;
  stock: number;
  minStock: number;
  trackStock: boolean;
  stockStatus: StockStatus;
  stockValue: number;
}

export interface StockMovement {
  id: string;
  productId: string;
  productName: string;
  storeId?: string | null;
  storeName: string;
  type: MovementType;
  quantity: number;
  previousQty: number;
  newQty: number;
  reason: string;
  reference: string;
  date: string;
  notes: string;
}

export interface StockAlert {
  id: string;
  productId: string;
  productName: string;
  storeId?: string | null;
  storeName: string;
  type: AlertType;
  currentQty: number;
  minQty: number;
  viewed: boolean;
  resolved: boolean;
  date: string;
  resolvedDate?: string;
}

export interface InventoryOverview {
  totalProducts: number;
  inStock: number;
  lowStock: number;
  outOfStock: number;
  totalValue: number;
}

export interface AlertCounts {
  total: number;
  lowStock: number;
  outOfStock: number;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
  needsSetup?: boolean;
}

export interface ProductFilters {
  page?: number;
  limit?: number;
  storeId?: number;
  status?: StockStatus;
  search?: string;
}

export interface MovementFilters {
  page?: number;
  limit?: number;
  productId?: number;
  storeId?: number;
  type?: MovementType;
  startDate?: string;
  endDate?: string;
}

export interface AlertFilters {
  page?: number;
  limit?: number;
  storeId?: number;
  type?: AlertType;
  resolved?: boolean;
}

export interface CreateMovementData {
  productId: number;
  storeId?: number;
  type: MovementType;
  quantity: number;
  reason?: string;
  reference?: string;
  notes?: string;
}

export interface UpdateProductStockData {
  stock?: number;
  minStock?: number;
  trackStock?: boolean;
}

// ============================================
// SERVICE
// ============================================

class InventoryService {
  // ============================================
  // SETUP
  // ============================================

  async setupTables(): Promise<{
    success: boolean;
    message: string;
    created: string[];
  }> {
    try {
      const response = await api.post('/admin/shop/inventory/setup');
      return response.data;
    } catch (error) {
      console.error('Setup inventory tables error:', error);
      throw error;
    }
  }

  // ============================================
  // OVERVIEW
  // ============================================

  async getOverview(storeId?: number): Promise<{ success: boolean; data: InventoryOverview; needsSetup?: boolean }> {
    try {
      const queryParams = storeId ? `?storeId=${storeId}` : '';
      const response = await api.get(`/admin/shop/inventory/overview${queryParams}`);
      return response.data;
    } catch (error) {
      console.error('Get inventory overview error:', error);
      throw error;
    }
  }

  // ============================================
  // PRODUCTS
  // ============================================

  async getProducts(params?: ProductFilters): Promise<PaginatedResponse<InventoryProduct>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', String(params.page));
      if (params?.limit) queryParams.append('limit', String(params.limit));
      if (params?.storeId) queryParams.append('storeId', String(params.storeId));
      if (params?.status) queryParams.append('status', params.status);
      if (params?.search) queryParams.append('search', params.search);

      const response = await api.get(`/admin/shop/inventory/products?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get inventory products error:', error);
      throw error;
    }
  }

  async updateProductStock(id: string | number, data: UpdateProductStockData): Promise<{
    success: boolean;
    message: string;
  }> {
    try {
      const response = await api.put(`/admin/shop/inventory/products/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Update product stock error:', error);
      throw error;
    }
  }

  // ============================================
  // MOVEMENTS
  // ============================================

  async getMovements(params?: MovementFilters): Promise<PaginatedResponse<StockMovement>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', String(params.page));
      if (params?.limit) queryParams.append('limit', String(params.limit));
      if (params?.productId) queryParams.append('productId', String(params.productId));
      if (params?.storeId) queryParams.append('storeId', String(params.storeId));
      if (params?.type) queryParams.append('type', params.type);
      if (params?.startDate) queryParams.append('startDate', params.startDate);
      if (params?.endDate) queryParams.append('endDate', params.endDate);

      const response = await api.get(`/admin/shop/inventory/movements?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get movements error:', error);
      throw error;
    }
  }

  async createMovement(data: CreateMovementData): Promise<{
    success: boolean;
    message: string;
    data: { id: number; previousStock: number; newStock: number };
  }> {
    try {
      const response = await api.post('/admin/shop/inventory/movements', data);
      return response.data;
    } catch (error) {
      console.error('Create movement error:', error);
      throw error;
    }
  }

  // ============================================
  // ALERTS
  // ============================================

  async getAlerts(params?: AlertFilters): Promise<PaginatedResponse<StockAlert>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', String(params.page));
      if (params?.limit) queryParams.append('limit', String(params.limit));
      if (params?.storeId) queryParams.append('storeId', String(params.storeId));
      if (params?.type) queryParams.append('type', params.type);
      if (params?.resolved !== undefined) queryParams.append('resolved', String(params.resolved));

      const response = await api.get(`/admin/shop/inventory/alerts?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get alerts error:', error);
      throw error;
    }
  }

  async getAlertCounts(storeId?: number): Promise<{ success: boolean; data: AlertCounts }> {
    try {
      const queryParams = storeId ? `?storeId=${storeId}` : '';
      const response = await api.get(`/admin/shop/inventory/alerts/count${queryParams}`);
      return response.data;
    } catch (error) {
      console.error('Get alert counts error:', error);
      throw error;
    }
  }

  async resolveAlert(id: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.put(`/admin/shop/inventory/alerts/${id}/resolve`);
      return response.data;
    } catch (error) {
      console.error('Resolve alert error:', error);
      throw error;
    }
  }

  async viewAlert(id: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.put(`/admin/shop/inventory/alerts/${id}/view`);
      return response.data;
    } catch (error) {
      console.error('View alert error:', error);
      throw error;
    }
  }

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  getMovementTypeLabel(type: MovementType): string {
    const labels: Record<MovementType, string> = {
      entrada: 'Entrada',
      saida: 'Saida',
      ajuste: 'Ajuste',
      venda: 'Venda',
      devolucao: 'Devolucao',
    };
    return labels[type] || type;
  }

  getMovementTypeColor(type: MovementType): 'success' | 'error' | 'warning' | 'info' | 'primary' {
    const colors: Record<MovementType, 'success' | 'error' | 'warning' | 'info' | 'primary'> = {
      entrada: 'success',
      saida: 'error',
      ajuste: 'warning',
      venda: 'info',
      devolucao: 'primary',
    };
    return colors[type] || 'primary';
  }

  getAlertTypeLabel(type: AlertType): string {
    const labels: Record<AlertType, string> = {
      estoque_baixo: 'Estoque Baixo',
      sem_estoque: 'Sem Estoque',
      excesso: 'Excesso de Estoque',
    };
    return labels[type] || type;
  }

  getAlertTypeColor(type: AlertType): 'warning' | 'error' | 'info' {
    const colors: Record<AlertType, 'warning' | 'error' | 'info'> = {
      estoque_baixo: 'warning',
      sem_estoque: 'error',
      excesso: 'info',
    };
    return colors[type] || 'warning';
  }

  getStockStatusLabel(status: StockStatus): string {
    const labels: Record<StockStatus, string> = {
      ok: 'Estoque OK',
      low: 'Estoque Baixo',
      out: 'Sem Estoque',
    };
    return labels[status] || status;
  }

  getStockStatusColor(status: StockStatus): 'success' | 'warning' | 'error' {
    const colors: Record<StockStatus, 'success' | 'warning' | 'error'> = {
      ok: 'success',
      low: 'warning',
      out: 'error',
    };
    return colors[status] || 'success';
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-CV', {
      style: 'currency',
      currency: 'CVE',
    }).format(value);
  }

  formatDate(date: string): string {
    return new Date(date).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
}

export default new InventoryService();
